// ============================================================================
// Confirm a manual provider op (Porter / intercity). The backend dispatched it as
// a task; the ops team actions it on the partner's app, then confirms here:
//   BOOK   → enter the partner's reference / tracking (+ trip) → booking goes live
//   CANCEL → acknowledge the partner has cancelled it → booking is cancelled
//   UPDATE → acknowledge the partner has applied the revision
// ============================================================================
import { Alert, Form, Input, Modal, Typography } from "antd";
import React, { useEffect } from "react";
import ModalBody, { FLUSH_MODAL, MODAL_BODY_STYLE } from "../../components/modal_body";
import { confirmManualOp } from "../logistics.api";
import { trackingUrlRules } from "../porter_link";
import { ConfirmOpBody, PendingManualOp } from "../../types";

interface Props {
  open: boolean;
  task: PendingManualOp | null;
  onClose: () => void;
  onConfirmed: () => void; // parent refetches the inbox + bookings
}

const OP_TITLE: Record<string, string> = {
  book: "Confirm booking",
  cancel: "Confirm cancellation",
  update: "Confirm modification",
};

const ConfirmOpModal: React.FC<Props> = ({ open, task, onClose, onConfirmed }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = React.useState(false);
  const isBook = task?.op === "book";
  // Confirming a Porter BOOK is the best moment to capture the live-tracking link — that link is
  // what lets the backend close the delivery out on its own. Optional, never blocking: we only warn
  // if the link that IS pasted carries no tracking details (see ../porter_link).
  const isPorter = task?.booking?.provider_type === "porter";

  // Pre-fill from what's already on the booking — the ops team often stamps the vehicle/driver (and
  // sometimes the ref) on the board before confirming, so don't make them re-type it here.
  useEffect(() => {
    if (!task) return;
    const b = task.booking;
    form.setFieldsValue({
      provider_ref: b?.provider_ref ?? undefined,
      tracking_url: b?.tracking_url ?? undefined,
      vehicle_number: b?.vehicle_number ?? undefined,
      driver_name: b?.driver_name ?? undefined,
      driver_number: b?.driver_number ?? undefined,
    });
  }, [task, form]);

  const handleOk = async () => {
    if (!task) return;
    let body: ConfirmOpBody = {};
    if (isBook) {
      const values = await form.validateFields();
      body = {
        provider_ref: values.provider_ref?.trim() || undefined,
        tracking_url: values.tracking_url?.trim() || undefined,
        vehicle_number: values.vehicle_number?.trim() || undefined,
        driver_name: values.driver_name?.trim() || undefined,
        driver_number: values.driver_number?.trim() || undefined,
      };
    }
    setSaving(true);
    const updated = await confirmManualOp(task.id, body);
    setSaving(false);
    if (updated) {
      onConfirmed();
      onClose();
    }
  };

  return (
    <Modal
      title={`${OP_TITLE[task?.op ?? "book"]} — booking #${task?.booking?.id ?? ""}`}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isBook ? "Confirm booking" : "Confirm"}
      confirmLoading={saving}
      destroyOnClose
      centered
      className={FLUSH_MODAL}
      bodyStyle={MODAL_BODY_STYLE}
    >
      <ModalBody>
        {task && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={task.task}
            description={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {task.booking.provider_type?.replace(/_/g, " ")} ·{" "}
                {task.booking.zoho_salesorder_number ||
                  (task.booking.sales_order_id ? `SO #${task.booking.sales_order_id}` : "—")}{" "}
                · {task.booking.origin_label || "—"} → {task.booking.destination_label || "—"}
              </Typography.Text>
            }
          />
        )}

        {isBook ? (
          <Form form={form} layout="vertical">
            <Form.Item
              label="Carrier's booking number (LR / AWB)"
              name="provider_ref"
              extra="The number the partner gave you — this is how we track this truck and cancel it later if needed."
            >
              <Input placeholder="e.g. ICL-LR-4521" maxLength={255} allowClear />
            </Form.Item>
            <Form.Item
              label={isPorter ? "Porter live-tracking link (optional)" : "Tracking URL"}
              name="tracking_url"
              rules={trackingUrlRules(isPorter)}
              extra={
                isPorter
                  ? "Share the trip from the Porter app and paste the link here and we'll mark this delivered automatically when Porter completes the trip. Leave it blank to close this delivery by hand as usual."
                  : undefined
              }
            >
              <Input placeholder="https://…" allowClear />
            </Form.Item>
            <Form.Item
              label="Vehicle number"
              name="vehicle_number"
              rules={[{ required: true, message: "Enter the vehicle number" }]}
            >
              <Input placeholder="e.g. KA01AB1234" maxLength={32} allowClear />
            </Form.Item>
            <Form.Item label="Driver name" name="driver_name">
              <Input maxLength={128} allowClear />
            </Form.Item>
            <Form.Item label="Driver number" name="driver_number">
              <Input maxLength={20} allowClear />
            </Form.Item>
          </Form>
        ) : (
          <Typography.Paragraph type="secondary">
            Confirm only once the partner has{" "}
            {task?.op === "cancel" ? "cancelled" : "applied the revision to"} this consignment at their
            end. This settles the task and updates the booking.
          </Typography.Paragraph>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ConfirmOpModal;
